import {RunsDataQuery} from '../../../../containers/RunsDataLoader';
import * as Filter from '../../../../util/filters';
import {isSystemMetric} from '../../../../util/metrics';
import {type DataDogPanelType} from '../../../../util/panelTypes';
import {sortToOrderString} from '../../../../util/queryts';
import {SingleQuery} from '../../../../util/queryTypes';
import {GROUP_BY_ALL_KEY} from '../../../../util/runTypes';

export type BucketedQueryType =
  | 'BucketedQuery'
  | 'BucketedQueryId'
  | 'BucketedBackendLinesQuery'
  | 'BucketedBackendLinesQueryId'
  | 'ShadowedBackendLinesQuery'
  | 'GroupedBucketedQuery'
  | 'GroupedBucketedQueryId';

export type QueryVars = {
  bins?: number; // not used by the backend, included for datadog segmentation
  bucketedHistorySpecs: string[];
  configKeys: string[] | undefined;
  enableBasic: boolean;
  enableConfig: boolean;
  enableSummary: boolean;
  entityName: string;
  expressions: string[] | undefined;
  filters: string;
  groupKeys: string[];
  groupLevel: number;
  internalId: string;
  isSystemMetrics: boolean | null; // not used, included for datadog segmentation
  limit: number;
  maxGroups: number | undefined;
  order: string;
  panelType: DataDogPanelType; // client-only, like bins
  projectName: string;
  summaryKeys: string[] | undefined;
};

export function queryVars(
  runsDataQuery: Exclude<RunsDataQuery, 'queries'>,
  query: SingleQuery,
  nBuckets: number,
  queryType: BucketedQueryType,
  expressions: string[] | undefined,
  panelType: DataDogPanelType
): QueryVars {
  const filters = JSON.stringify(Filter.toMongo(query.filters));
  const order = sortToOrderString(query.sort);

  let limit = runsDataQuery.page?.size || 10;
  if (Array.isArray(limit)) {
    console.error('Unexpected query limit of type array', {
      entityName: runsDataQuery.entityName,
      projectName: runsDataQuery.projectName,
    });
    limit = limit.flat(Infinity)[0] ?? 10;
  }

  // Replace config.None with GROUP_BY_ALL_KEY because config.None is not a valid group key when grouping in the backend
  // This happens when selecting "None" in the group by dropdown
  // TODO: Move this replacement upstream to where groupKeys are initially populated, once we have sufficient test
  // coverage to safely make that change.
  const groupKeys = [
    'GroupedBucketedQuery',
    'GroupedBucketedQueryWithInternalId',
  ].includes(queryType)
    ? (runsDataQuery.groupKeys?.map(k =>
        k === 'config.None' ? 'config.' + GROUP_BY_ALL_KEY.name : k
      ) ?? [])
    : [];

  return {
    bins: nBuckets,
    bucketedHistorySpecs:
      runsDataQuery.bucketedHistorySpecs?.map(bhs =>
        JSON.stringify({...bhs, bins: nBuckets, xAxis: bhs.xAxis ?? '_step'})
      ) ?? [],
    configKeys: runsDataQuery.configKeys,
    enableBasic: true,
    enableConfig: runsDataQuery.configKeys != null,
    enableSummary: runsDataQuery.summaryKeys != null,
    entityName: runsDataQuery.entityName,
    expressions,
    filters,
    groupKeys,
    groupLevel: 0,
    internalId: query.internalProjectId ?? '',
    isSystemMetrics:
      runsDataQuery.bucketedHistorySpecs?.every(bhs =>
        bhs.keys.some(isSystemMetric)
      ) ?? null,
    limit: runsDataQuery.page?.size || 10,
    maxGroups: query.maxGroups,
    order,
    panelType,
    projectName: runsDataQuery.projectName,
    summaryKeys: runsDataQuery.summaryKeys,
  };
}
